/**
 * BaseModal.Inline - Inline Content Plugin for BaseModal
 * Handles moving content from DOM to modal and back
 *
 * Features:
 * - Moves content instead of copying (preserves events and state)
 * - Uses placeholder comment to mark original position
 * - Automatically handles visibility
 *
 * @requires BaseModal.js
 * @version 1.0.0
 */

'use strict';

import { waitForAnimation } from './BaseModal/animations.js';

// ========================================
// CONSTANTS & DEFAULTS
// ========================================

/**
 * Default configuration for inline modal
 * @type {Object}
 */
const DEFAULTS = {
    selector: '[data-basemodal-inline]', // Attribute containing the ID of the content to show
    theme: 'light',
    animation: 'zoom',
    size: 'default',
    debugMode: false,
    locales: {
        close: 'Close'
    }
};


// ========================================
// MAIN CLASS
// ========================================

/**
 * BaseModal.Inline Class
 */
class BaseModalInline {
    /**
     * Create an inline modal instance
     * @param {Object} config - Configuration options
     */
    constructor(config = {}) {
        this.config = { ...DEFAULTS, ...config };
        this.activePlaceholders = new Map(); // Map<modalId, { placeholder: Comment, originalParent: HTMLElement, element: HTMLElement }>

        if (this.config.debugMode) {
            console.log('BaseModal.Inline initialized with config:', this.config);
        }
    }

    // ========================================
    // INITIALIZATION
    // ========================================

    /**
     * Initialize inline plugin
     * Attaches event listeners to document for delegation
     * @public
     */
    init() {
        this.attachEventListeners();

        if (this.config.debugMode) {
            console.log('BaseModal.Inline: Event listeners attached');
        }
    }

    /**
     * Attach click event listeners using delegation
     * @private
     */
    attachEventListeners() {
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest(this.config.selector);
            if (trigger) {
                e.preventDefault();
                const contentId = trigger.getAttribute('data-basemodal-inline');
                this.show(contentId, trigger);
            }
        });
    }

    // ========================================
    // MODAL DISPLAY
    // ========================================

    /**
     * Show inline content in modal
     * @param {string} contentId - ID of the element to show
     * @param {HTMLElement} [trigger] - Element that triggered the modal
     * @returns {Promise<void>}
     * @public
     */
    async show(contentId, trigger = null) {
        const element = document.getElementById(contentId);

        if (!element) {
            console.error(`BaseModal.Inline: Element with ID "${contentId}" not found`);
            return;
        }

        // Generate unique modal ID
        const modalId = 'bm-inline-' + Date.now();

        if (this.config.debugMode) {
            console.log(`BaseModal.Inline: Moving element #${contentId} to modal #${modalId}`);
        }

        // Create Placeholder
        const placeholder = document.createComment(`BaseModal Placeholder for #${contentId}`);
        const originalParent = element.parentNode;

        // Insert placeholder before the element
        originalParent.insertBefore(placeholder, element);

        // Store reference for cleanup
        this.activePlaceholders.set(modalId, {
            placeholder,
            originalParent,
            element,
            originalDisplay: element.style.display
        });

        // Prepare Element
        // Make sure it's visible if it was hidden
        // We don't change display property yet, we'll do it after moving

        // Create Modal Structure
        const modalHTML = this.generateModalHTML(modalId);
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Move Element to Modal
        const modal = document.getElementById(modalId);
        const contentWrapper = modal.querySelector('.bm-inline-content-wrapper');

        contentWrapper.appendChild(element);

        // Ensure visibility
        element.style.display = 'block';
        element.classList.add('bm-inline-active');

        // Show Modal
        try {
            // Get overrides from trigger if available
            const overrides = {};
            if (trigger) {
                if (trigger.dataset.theme) overrides.theme = trigger.dataset.theme;
                if (trigger.dataset.size) overrides.size = trigger.dataset.size;
                if (trigger.dataset.animation) overrides.animation = trigger.dataset.animation;
            }

            await window.BaseModal.show(modalId, {
                theme: overrides.theme || this.config.theme,
                animation: overrides.animation || this.config.animation,
                size: overrides.size || this.config.size,
                awaitCloseAnimation: true,
                onClose: () => {
                    this.cleanup(modalId);
                }
            });

        } catch (error) {
            console.error('BaseModal.Inline: Error showing modal:', error);
            // Cleanup if show fails
            this.cleanup(modalId);
        }
    }

    // ========================================
    // CLEANUP & RESTORE
    // ========================================

    /**
     * Restore content to original position and cleanup
     * @param {string} modalId - ID of the modal
     * @private
     */
    cleanup(modalId) {
        const data = this.activePlaceholders.get(modalId);

        if (!data) {
            if (this.config.debugMode) {
                console.warn(`BaseModal.Inline: No cleanup data found for ${modalId}`);
            }
            return;
        }

        const modal = document.getElementById(modalId);

        if (!modal) {
            this.restoreContent(data);
            this.activePlaceholders.delete(modalId);
            return;
        }

        // Wait for animation to complete before restoring content
        waitForAnimation(this, modal, () => {
            this.restoreContent(data);

            // Remove modal from DOM
            modal.remove();

            // Clear map
            this.activePlaceholders.delete(modalId);
        });
    }

    /**
     * Helper to restore content to original position
     * @param {Object} data - Placeholder data
     * @private
     */
    restoreContent(data) {
        const {
            placeholder,
            originalParent,
            element,
            originalDisplay
        } = data;

        if (this.config.debugMode) {
            console.log(`BaseModal.Inline: Restoring element #${element.id}`);
        }

        // Move element back
        if (originalParent.contains(placeholder)) {
            originalParent.insertBefore(element, placeholder);
            placeholder.remove();
        } else {
            // Fallback if placeholder is gone
            originalParent.appendChild(element);
        }

        // Restore original state
        element.style.display = originalDisplay;
        element.classList.remove('bm-inline-active');
    }

    // ========================================
    // HTML GENERATION
    // ========================================

    /**
     * Generate modal HTML structure
     * @param {string} modalId - Unique modal ID
     * @returns {string} HTML string
     * @private
     */
    generateModalHTML(modalId) {
        return `
<div id="${modalId}" class="bm-modal bm-inline-modal" aria-hidden="true">
    <div tabindex="-1" class="bm-modal__overlay">
        <div role="dialog" class="bm-modal__container bm-modal__container--inline" aria-modal="true">
            <button aria-label="Закрыть"
                    class="bm-modal__close"
                    data-basemodal-close>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                    <path d='M18.5297 17.47C18.8227 17.763 18.8227 18.238 18.5297 18.531C18.3837 18.677 18.1917 18.751 17.9997 18.751C17.8077 18.751 17.6158 18.678 17.4698 18.531L11.9997 13.061L6.52975 18.531C6.38375 18.677 6.19175 18.751 5.99975 18.751C5.80775 18.751 5.61575 18.678 5.46975 18.531C5.17675 18.238 5.17675 17.763 5.46975 17.47L10.9398 12L5.46975 6.53005C5.17675 6.23705 5.17675 5.76202 5.46975 5.46902C5.76275 5.17602 6.23775 5.17602 6.53075 5.46902L12.0008 10.939L17.4707 5.46902C17.7637 5.17602 18.2387 5.17602 18.5317 5.46902C18.8247 5.76202 18.8247 6.23705 18.5317 6.53005L13.0617 12L18.5297 17.47Z'
                          fill='currentColor'/>
                </svg>
            </button>

            <div class="bm-modal__content bm-inline-content-wrapper">
                <!-- Content will be moved here -->
            </div>
        </div>
        <div class="bm-modal__bg" aria-hidden="true" data-basemodal-close></div>
    </div>
</div>`;
    }
}

export default BaseModalInline;
